import { useEffect, useState } from "react";
import { createFileRoute, useNavigate, useRouteContext, useSearch } from "@tanstack/react-router";
import { toast } from "sonner";
import { ExternalLink, FolderUp, HardDrive, Loader2 } from "lucide-react";

import { loadGoogleKeyInfo, setGoogleKey, type GoogleKeyInfo } from "@/braining/google-key";
import {
  disconnectGoogle,
  finishGoogleAuth,
  loadGoogleOAuthInfo,
  setGoogleClient,
  startGoogleAuth,
  type GoogleOAuthInfo,
} from "@/braining/google-oauth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/google")({
  // Возврат от Google приходит сюда же, с кодом согласия в адресе: заводить
  // ради одного обмена собственный серверный маршрут — лишний механизм.
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === "string" ? search.code : undefined,
    state: typeof search.state === "string" ? search.state : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: GoogleDrivePage,
});

/**
 * Два доступа к Google, и они разные не по недосмотру.
 *
 * Чтение папок с материалами — сервисным аккаунтом: агентству достаточно один
 * раз расшарить папку на его почту, поэтому почта здесь показана крупно, её
 * копируют и отдают Ане.
 *
 * Запись готовых калькуляторов — OAuth от имени человека. Сервисный аккаунт на
 * это не годится: у него нет своей квоты на Drive, и созданный им файл класть
 * некуда. Область `drive.file` (только свои файлы) верификации не требует —
 * ровно та причина, по которой OAuth когда-то отвергли, здесь не работает.
 */
function GoogleDrivePage() {
  const [info, setInfo] = useState<GoogleKeyInfo | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const [oauth, setOauth] = useState<GoogleOAuthInfo | null>(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const { user } = useRouteContext({ from: "__root__" });
  // Приложение Google — настройка инстанса: его заводит и меняет админ.
  // Подключает общий аккаунт агентства тоже админ; остальным экран показывает, куда уезжают калькуляторы.
  const admin = user?.role === "admin";
  const [busy, setBusy] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  // Форма приложения открыта, пока его не сохранили; потом прячется за
  // «Заменить приложение», чтобы пустые поля не выглядели как «ничего нет».
  const [showClientForm, setShowClientForm] = useState(false);

  const search = useSearch({ from: "/google" });
  const navigate = useNavigate();

  useEffect(() => {
    void loadGoogleKeyInfo()
      .then(setInfo)
      .catch((e: unknown) => console.error("[google] не удалось прочитать состояние ключа", e));
    void loadGoogleOAuthInfo()
      .then((next) => {
        setOauth(next);
        setShowClientForm(!next.hasClient);
      })
      .catch((e: unknown) => console.error("[google] не удалось прочитать подключение", e));
  }, []);

  // Возврат от Google: код меняем на постоянный доступ и сразу убираем его из
  // адресной строки — код одноразовый, и оставлять его в истории браузера ни к
  // чему.
  useEffect(() => {
    if (search.error) {
      setAuthError(`Google отказал: ${search.error}`);
      void navigate({
        to: "/google",
        search: { code: undefined, state: undefined, error: undefined },
        replace: true,
      });
      return;
    }
    if (!search.code || !search.state) return;

    const { code, state } = search;
    void navigate({
      to: "/google",
      search: { code: undefined, state: undefined, error: undefined },
      replace: true,
    });
    setBusy(true);
    finishGoogleAuth({ data: { code, state } })
      .then((next) => {
        setOauth(next);
        setAuthError(null);
        toast.success("Google подключён");
      })
      .catch((e: unknown) => {
        console.error("[google] подключение не удалось", e);
        setAuthError(e instanceof Error ? e.message : "Подключение не удалось");
      })
      .finally(() => setBusy(false));
  }, [search.code, search.state, search.error, navigate]);

  async function saveClient() {
    setBusy(true);
    setAuthError(null);
    try {
      setOauth(
        await setGoogleClient({
          data: { clientId: clientId.trim(), clientSecret: clientSecret.trim() },
        }),
      );
      setClientId("");
      setClientSecret("");
      setShowClientForm(false);
      toast.success("Данные приложения сохранены");
    } catch (e) {
      console.error("[google] не удалось сохранить приложение", e);
      setAuthError(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  async function connect() {
    setBusy(true);
    setAuthError(null);
    try {
      const { url } = await startGoogleAuth();
      window.location.href = url;
    } catch (e) {
      console.error("[google] не удалось начать подключение", e);
      setAuthError(e instanceof Error ? e.message : "Не удалось начать подключение");
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    try {
      setOauth(await disconnectGoogle());
      toast.success("Аккаунт отключён");
    } catch (e) {
      console.error("[google] не удалось отключить", e);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const json = draft.trim();
    if (json.length < 40) return;

    setSaving(true);
    try {
      setInfo(await setGoogleKey({ data: { json } }));
      setDraft("");
      toast.success("Ключ сохранён");
    } catch (e) {
      console.error("[google] не удалось сохранить ключ", e);
      // Сервер объясняет, что не так: не JSON, не тот файл, нет private_key.
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить ключ");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Google Drive</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Куда уезжают готовые калькуляторы и откуда система читает материалы проекта. Аккаунт для
          выгрузки и приложение Google подключает администратор — обычно один раз, общий на всех.
        </p>
      </div>

      {/* Сервисный ключ — чтение чужих папок с материалами, дело инстанса. */}
      {user?.role !== "admin" ? null : (
        <Card>
          <CardContent className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <HardDrive className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">Доступ к Google Drive</span>
              {info && !info.isSet && (
                <span className="text-xs text-warning-ink">не задан — папки не читаются</span>
              )}
            </div>

            {info?.isSet ? (
              <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
                <p className="text-muted-foreground">Папку с проектами нужно расшарить на почту:</p>
                <p className="mt-1 font-mono text-xs break-all">{info.clientEmail}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Права «Просмотр». Проще расшарить одну родительскую папку или общий диск целиком —
                  тогда вложенные будут доступны сами.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Нужен ключ сервисного аккаунта: JSON-файл из Google Cloud. Вставьте его содержимое
                целиком — почта, на которую агентство расшарит папку, появится здесь после
                сохранения.
              </p>
            )}

            <div className="space-y-2">
              <Textarea
                value={draft}
                rows={3}
                placeholder='{"type": "service_account", "project_id": …}'
                className="font-mono text-xs"
                onChange={(e) => setDraft(e.target.value)}
              />
              <Button
                variant={info?.isSet ? "outline" : "default"}
                onClick={() => void save()}
                disabled={draft.trim().length < 40 || saving}
              >
                {saving && <Loader2 className="size-4 animate-spin" />}
                {info?.isSet ? "Заменить ключ" : "Сохранить ключ"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="flex items-center gap-2">
            <FolderUp className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium">Выгрузка калькуляторов</span>
            {oauth && !oauth.connected && (
              <span className="text-xs text-warning-ink">не подключено — выгрузка не работает</span>
            )}
          </div>

          {/*
            Подключение одно — аккаунт агентства. Личных дисков нет по решению
            владельца 23.09: Workspace у Braining нет, зато есть один
            оплаченный ящик, в который ходят все. Там калькуляторы видит
            каждый, кому они нужны, и они не уходят вместе с человеком.
          */}
          <p className="text-sm text-muted-foreground">
            Готовые калькуляторы уезжают в Google-таблицы на Диск аккаунта агентства
            {oauth?.folderName ? `, в папку «${oauth.folderName}»` : ""}. Подключает его
            администратор один раз — калькуляторы видны всем, у кого есть доступ к этому аккаунту.
            Сервисный аккаунт выше для этого не годится: у него нет своего места на Google Диске, и
            созданный им файл класть некуда.
          </p>

          {authError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive-ink">
              {authError}
            </p>
          )}

          {/* Что уже настроено — двумя строками, приложение и аккаунт отдельно.
              Раньше экран показывал только пустую форму, и по ней нельзя было
              понять, сохранено приложение или нет. */}
          {oauth?.hasClient && (
            <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
              <div>
                <p className="text-muted-foreground">Приложение Google сохранено:</p>
                <p className="mt-0.5 font-mono text-xs break-all">{oauth.clientId}</p>
              </div>

              {/*
                Куда именно уедет файл — до выгрузки, а не после. Жалоба Вики
                №4 от 21.09 была про это: «готовая таблица падает на Google
                Диск Кеши, а не того, кто её сгенерировал». Лечится не личными
                дисками, а тем, что аккаунт один и общий, и на экране написано
                чей.
              */}
              {oauth.connected ? (
                <div className="border-t pt-2">
                  <p className="text-muted-foreground">
                    Аккаунт агентства:{" "}
                    <span className="font-medium text-foreground">{oauth.account ?? "Google"}</span>
                    . Калькуляторы уезжают туда, в папку «{oauth.folderName}».
                  </p>
                </div>
              ) : (
                <p className="border-t pt-2 text-warning-ink">
                  Аккаунт ещё не подключён — осталось нажать «Подключить аккаунт агентства».
                </p>
              )}
            </div>
          )}

          {showClientForm && admin && (
            <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
              <p>
                Нужно приложение Google: консоль Google Cloud → «Учётные данные» → «Идентификатор
                клиента OAuth» → тип «Веб-приложение».
              </p>
              <p className="mt-2">
                В разрешённые адреса возврата впишите ровно это:{" "}
                <span className="font-mono text-xs break-all">
                  {oauth?.redirectUri ?? "не задан PUBLIC_URL"}
                </span>
              </p>
              <p className="mt-2">
                Запрашиваем самый узкий доступ — только к файлам, которые создали сами. Проверку
                приложения Google для него не требует.
              </p>
              <p className="mt-2">
                Приложение нужно опубликовать — статус «В продакшене». В статусе «Тестирование»
                Google отзывает доступ через неделю, и выгрузка перестанет работать.
              </p>
            </div>
          )}

          {showClientForm && admin && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                value={clientId}
                placeholder="Идентификатор клиента"
                className="font-mono text-xs"
                onChange={(e) => setClientId(e.target.value)}
              />
              <Input
                value={clientSecret}
                placeholder="Секрет клиента"
                className="font-mono text-xs"
                onChange={(e) => setClientSecret(e.target.value)}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {showClientForm && admin && (
              <Button
                onClick={() => void saveClient()}
                disabled={busy || clientId.trim().length < 10 || clientSecret.trim().length < 5}
              >
                {busy && <Loader2 className="size-4 animate-spin" />}
                {oauth?.hasClient ? "Заменить приложение" : "Сохранить приложение"}
              </Button>
            )}

            {oauth?.hasClient && admin && (
              <Button
                variant={oauth.connected ? "outline" : "default"}
                onClick={() => void connect()}
                disabled={busy}
              >
                {busy && <Loader2 className="size-4 animate-spin" />}
                {oauth.connected ? "Подключить другой аккаунт" : "Подключить аккаунт агентства"}
              </Button>
            )}

            {oauth?.hasClient && admin && (
              <Button
                variant="ghost"
                onClick={() => {
                  setShowClientForm((v) => !v);
                  setClientId("");
                  setClientSecret("");
                }}
                disabled={busy}
              >
                {showClientForm ? "Не менять приложение" : "Заменить приложение"}
              </Button>
            )}

            {oauth?.connected && admin && (
              <Button variant="ghost" onClick={() => void disconnect()} disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                Отключить
              </Button>
            )}
          </div>

          {showClientForm && oauth?.connected && (
            <p className="text-xs text-warning-ink">
              Смена приложения обнулит подключение: постоянный доступ выдан прежним приложением.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
